// ARM loan terms on the pricing engine page, split by fixed period AND
// adjustment cadence (LenderDocsProcessors/docs/plans/ppe-arm-cadence.md):
// armN6 = fixed for N years, then adjusts every 6 months; armN1 = fixed for
// N years, then adjusts every year. The calculator still accepts the older
// fixed-period-only values (arm3/arm5/arm7/arm10, either cadence) for API
// callers, but the page no longer sends them.

export const ARM_TERM_OPTIONS = [
  { value: "arm31", label: "3/1 ARM" },
  { value: "arm36", label: "3/6 ARM" },
  { value: "arm51", label: "5/1 ARM" },
  { value: "arm56", label: "5/6 ARM" },
  { value: "arm71", label: "7/1 ARM" },
  { value: "arm76", label: "7/6 ARM" },
  { value: "arm101", label: "10/1 ARM" },
  { value: "arm106", label: "10/6 ARM" },
];

// The default when the loan officer switches the amortization to ARM.
export const DEFAULT_ARM_TERM = "arm56";

// armFixedTerm the pricing request must carry for each ARM loanTerm. The
// fixed-period-only values stay here so replayed requests keep validating.
export const ARM_TERM_YEARS = {
  arm31: 3,
  arm36: 3,
  arm51: 5,
  arm56: 5,
  arm71: 7,
  arm76: 7,
  arm101: 10,
  arm106: 10,
  arm3: 3,
  arm5: 5,
  arm7: 7,
  arm10: 10,
};

// Saved searches from before the split stored the fixed-period-only values.
// Restore them as the semiannual product, the common case (plan D1).
const LEGACY_ARM_TERMS = {
  arm3: "arm36",
  arm5: "arm56",
  arm7: "arm76",
  arm10: "arm106",
};

export function restoreArmTerm(loanTerm) {
  return LEGACY_ARM_TERMS[loanTerm] || loanTerm;
}

export function armTermLabel(loanTerm) {
  const option = ARM_TERM_OPTIONS.find(({ value }) => value === loanTerm);
  return option ? option.label : "";
}

const INDEX_LABELS = { PrimeRate: "Prime", TBill: "T-Bill" };

// Short cadence label for an ARM product, e.g. "5/6 SOFR" or "5/1 CMT", built
// from the product's ARM fields (fixedperiod, adjustmentperiod, index) — never
// parsed from the product name. Annual cadence is stored as 1 (years) by the
// ratesheet parsers and as 12 (months) by API-sourced products.
export function armCadenceLabel(arm) {
  if (!arm || typeof arm !== "object") return "";
  const fixed = Number(arm.fixedperiod);
  if (!Number.isFinite(fixed) || fixed <= 0) return "";
  const adjustment = Number(arm.adjustmentperiod);
  const index = INDEX_LABELS[arm.index] || arm.index || "";
  let shape;
  if (adjustment === 1 || adjustment === 12) {
    shape = `${fixed}/1`;
  } else if (Number.isFinite(adjustment) && adjustment > 0) {
    shape = `${fixed}/${adjustment}`;
  } else {
    shape = `${fixed}-year`;
  }
  return index ? `${shape} ${index}` : `${shape} ARM`;
}

// When a cadence search returns nothing and every connected lender's reason is
// that it has no product of that ARM type (the calculator says "<lender> has
// no 7/1 ARM (...) products"), say so plainly instead of the generic
// "No qualified products." Returns "" when that is not the situation.
export function noArmProductsMessage(loanTerm, ineligibleByLender) {
  const label = armTermLabel(loanTerm);
  if (!label) return "";
  const reasons = Object.values(ineligibleByLender || {});
  if (reasons.length === 0) return "";
  const marker = `has no ${label} `;
  const allLackTerm = reasons.every((reason) =>
    String((reason && reason.LenderLevelReason) || "").includes(marker)
  );
  return allLackTerm
    ? `None of your connected lenders has a ${label} product right now.`
    : "";
}
